/**
 * Utility functions for handling image URLs in the application
 * This centralizes all image URL construction to ensure consistency
 */

import { API } from '../App';

/**
 * Get the base URL (without /api suffix)
 */
const getBaseUrl = () => API.replace('/api', '');

/**
 * Constructs the correct full URL for images stored on the server
 * Handles various path formats:
 * - Full URLs (https://...) - returned as-is
 * - API paths (/api/uploads/...) - prepends base URL
 * - Legacy paths (/uploads/...) - prepends base URL with /api
 * - Relative paths (uploads/...) - prepends base URL with /api/
 * 
 * @param {string} imagePath - The image path from database
 * @returns {string|null} - Full URL or null if no path
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  const baseUrl = getBaseUrl();
  
  // For API paths (/api/uploads/...), prepend the base URL
  if (imagePath.startsWith('/api/')) {
    return `${baseUrl}${imagePath}`;
  }
  
  // For legacy paths (/uploads/...), add /api prefix
  if (imagePath.startsWith('/uploads/')) {
    return `${baseUrl}/api${imagePath}`;
  }
  
  // For relative paths without leading slash (uploads/...)
  if (imagePath.startsWith('uploads/')) {
    return `${baseUrl}/api/${imagePath}`;
  }
  
  // For any other path, assume it needs /api prefix
  if (!imagePath.startsWith('/')) {
    return `${baseUrl}/api/${imagePath}`;
  }
  
  return `${baseUrl}${imagePath}`;
};

/**
 * Get founder image URL with fallback
 * @param {Object} founder - Founder object with image or image_url fields
 * @returns {string} - Image URL or fallback
 */
export const getFounderImageUrl = (founder) => {
  // Debug logging for troubleshooting
  if (process.env.NODE_ENV !== 'production') {
    console.log('getFounderImageUrl input:', { 
      image: founder?.image, 
      image_url: founder?.image_url 
    });
  }
  
  // First check for uploaded image path
  if (founder?.image) {
    const url = getImageUrl(founder.image);
    if (process.env.NODE_ENV !== 'production') {
      console.log('getFounderImageUrl using image field:', url);
    }
    return url;
  }
  // Fallback to image_url field (legacy external URLs)
  if (founder?.image_url) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('getFounderImageUrl using image_url fallback:', founder.image_url);
    }
    return founder.image_url;
  }
  // Default fallback
  return "https://images.unsplash.com/photo-1665111912062-87bde7daedc7?w=400";
};

export default { getImageUrl, getFounderImageUrl };
